import React, { useEffect, useMemo, useState } from "react"

import { css, Theme } from "@emotion/react"

import {
	CustomerCartSite,
	isNonParentCustomerPart,
	NonParentCustomerPart,
	useCustomerQuickOrder,
	useCustomerQuickOrderSites,
	useIsUser,
	UserId,
	useOnlinePartDetails,
} from "@ncs/ncs-api"
import { addOrReplace } from "@ncs/ts-utils"
import {
	AnimatedEntrance,
	Box,
	Button,
	Callout,
	Divider,
	EventTracker,
	Icon,
	Link,
	LoadingSpinner,
	Paragraph,
	PartClearancePriceEcomm,
	PartImage,
	QuickOrderPrice,
	SearchableSelect,
	TrackingEvent,
	useScreenSizeMatch,
} from "@ncs/web-legos"

import { CartPart, useShopContext } from "~/contexts"
import { AddedToCartModalEcomm, PageContentWrapper, QuantityControl } from "~/shared-components"
import { useAnalytics } from "~/hooks/useAnalytics"
import { UnitMapping } from "~/shared-components/quantity-control/UnitMapping"

interface QuickOrderPartRowProperties {
	part: NonParentCustomerPart
	quantity: number
	onQuantityChange: (quantity: number, part: NonParentCustomerPart) => void
}

export const QuickOrder: React.FC = () => {
	const screenIsTiny = useScreenSizeMatch("xs")
	const [partIds, setPartIds] = useState<string[]>([])
	const [showAddedToCartModal, setShowAddedToCartModal] = useState(false)
	const [quickOrderList, quickOrderListLoading] = useCustomerQuickOrderSites()
	const [selectedOrder, setSelectedOrder] = useState<CustomerCartSite | null>(null)
	const [quickParts, quickPartsAreLoading] = useCustomerQuickOrder(
		selectedOrder?.customerId.toString()
	)
	const [, shopDispatch] = useShopContext()
	const [ordersCartState, setOrdersCartState] = useState<{
		[orderId: string]: CartPart[] | undefined
	}>({})
	const [showOrderAddedConfirmation, setShowOrderAddedConfirmation] = useState(false)
	const isDb = useIsUser(UserId.DrivenBrands)
	const { trackEvent } = useAnalytics()
	const onOrderChange = (order: CustomerCartSite | null) => {
		setSelectedOrder(order)
		setShowOrderAddedConfirmation(false)
		trackEvent(TrackingEvent.QUICK_ORDER_SELECT_SITE)
	}

	useEffect(() => {
		if (selectedOrder?.customerId && quickParts) {
			setOrdersCartState((prev) => ({
				...prev,
				[selectedOrder.customerId.toString()]: quickParts.flatMap((quickPart) => {
					if (!isNonParentCustomerPart(quickPart.onlinePart)) {
						return []
					} else {
						return [
							{
								part: quickPart.onlinePart,
								quantity: quickPart.defaultQuantity ?? 0,
							},
						]
					}
				}),
			}))
		}
	}, [selectedOrder?.customerId, quickParts])

	// If it's driven brands, all the sites have the same order so we'll default
	// select the first in the list.
	useEffect(() => {
		if (
			(isDb &&
				!selectedOrder?.customerId &&
				!quickOrderListLoading &&
				quickOrderList &&
				quickOrderList.length > 0 &&
				!showOrderAddedConfirmation) ||
			(quickOrderList && quickOrderList.length === 1 && !showOrderAddedConfirmation)
		) {
			setSelectedOrder(quickOrderList[0])
		}
	}, [
		isDb,
		selectedOrder?.customerId,
		quickOrderList,
		quickOrderListLoading,
		showOrderAddedConfirmation,
	])

	const onQuantityChange = (quantity: number, part: NonParentCustomerPart) => {
		if (selectedOrder && ordersCartState[selectedOrder.customerId.toString()]) {
			setOrdersCartState((prev) => {
				const prevOrderState = prev[selectedOrder.customerId.toString()]

				// Note that prevOrderState should always exist, this is just being cautious.
				return prevOrderState ?
						{
							...prev,
							[selectedOrder.customerId.toString()]: addOrReplace(
								{ part, quantity },
								prevOrderState,
								(cartPart) => cartPart.part.id === part.id
							),
						}
					:	prev
			})
		}
	}

	const addToCart = () => {
		if (selectedOrder) {
			const products = (ordersCartState[selectedOrder.customerId.toString()] ?? []).filter(
				({ quantity }) => quantity > 0
			)
			setShowOrderAddedConfirmation(true)
			setSelectedOrder(null)
			shopDispatch({
				type: "add parts to cart",
				payload: products,
			})
			setShowAddedToCartModal(true)
			setPartIds(products.map((product) => product.part.id))
		}
	}

	const currentOrderItemCount = useMemo(() => {
		const order =
			selectedOrder?.customerId ?
				ordersCartState[selectedOrder.customerId.toString()]
			:	undefined
		if (!order) return 0

		return order.reduce(
			(tally: number, { quantity }: { quantity: number }) => tally + quantity,
			0
		)
	}, [ordersCartState, selectedOrder?.customerId])

	return (
		<PageContentWrapper
			breadcrumbs={[
				{
					name: "Shop",
					to: "/shop",
				},
				{
					name: "Quick Order",
				},
			]}
		>
			<Paragraph mb={0.8} css={quickOrderTitleStyle}>
				Quick Order
			</Paragraph>
			{quickOrderListLoading && <LoadingSpinner />}

			{!!quickOrderList && quickOrderList.length === 0 && !quickOrderListLoading && (
				<Paragraph color="secondary" css={quickOrderParagraphStyle}>
					None of your sites are set up for Quick Order.
				</Paragraph>
			)}

			{!!quickOrderList && quickOrderList.length > 0 && !isDb && (
				<>
					<Paragraph mb={2} css={quickOrderParagraphStyle}>
						Order from your predetermined list of Parts and Chemicals.
					</Paragraph>

					<SearchableSelect
						options={quickOrderList ?? []}
						value={selectedOrder}
						getOptionSelected={(option) =>
							option.customerId === selectedOrder?.customerId
						}
						onItemSelect={(option) => onOrderChange(option)}
						label="Site List"
						placeholder="Search..."
						getOptionLabel={(option) =>
							`${option.name} - ${option.city}, ${option.state}`
						}
						isLoading={quickOrderListLoading}
						mb={2}
						maxWidth={40}
					/>

					<Divider mb={1} />
				</>
			)}

			{quickPartsAreLoading && <LoadingSpinner mt={3} text="Loading order parts..." />}

			{!quickPartsAreLoading &&
				!showOrderAddedConfirmation &&
				!!selectedOrder &&
				(ordersCartState[selectedOrder.customerId.toString()] ?? []).length === 0 && (
					<Paragraph small color="secondary">
						No items in this Quick Order.
					</Paragraph>
				)}
			{!quickPartsAreLoading &&
				!showOrderAddedConfirmation &&
				!!selectedOrder &&
				(ordersCartState[selectedOrder.customerId.toString()] ?? []).map(
					({ part, quantity }, i) => {
						return (
							<QuickOrderPartRow
								key={`${i}-${part.onlinePartNumber}`}
								part={part}
								quantity={quantity}
								onQuantityChange={onQuantityChange}
							/>
						)
					}
				)}

			<AnimatedEntrance show={showOrderAddedConfirmation}>
				<Box mt={2}>
					<Callout mb={2}>
						<Paragraph>
							<Icon
								icon="check-circle"
								color="success"
								family="solid"
								css={orderAddedCheckStyle}
							/>
							Items added to cart.
						</Paragraph>
					</Callout>
					<Paragraph maxWidth={50}>
						{isDb ?
							<>
								You can{" "}
								<button
									css={nestedButtonStyle}
									onClick={() => setShowOrderAddedConfirmation(false)}
								>
									add additional Quick Order parts
								</button>
								, or use the search above to browse your entire catalog, or{" "}
								<EventTracker
									events={[
										TrackingEvent.QO_GO_TO_CART,
										TrackingEvent.GO_TO_CART,
									]}
									trackEvent={trackEvent}
								>
									<Link to="/shop/cart-summary">go to your cart</Link>
								</EventTracker>{" "}
								to checkout.
							</>
						:	<>
								Choose another Quick Order from the list above, or{" "}
								<EventTracker
									events={[
										TrackingEvent.QO_GO_TO_CART,
										TrackingEvent.GO_TO_CART,
									]}
									trackEvent={trackEvent}
								>
									<Link to="/shop/cart-summary">go to your cart</Link>
								</EventTracker>{" "}
								to checkout.
							</>
						}
					</Paragraph>
				</Box>
			</AnimatedEntrance>

			{!quickPartsAreLoading && !!selectedOrder && !showOrderAddedConfirmation && (
				<>
					<Paragraph
						textAlign="right"
						mt={3}
						mb={1}
						css={css`
							text-align: right;
						`}
					>
						<strong>{currentOrderItemCount} items</strong> to be added
					</Paragraph>
					<Box
						display="flex"
						justifyContent="flex-end"
						flexWrap="wrap"
						alignItems="flex-start"
					>
						<EventTracker
							event={TrackingEvent.ADD_ITEMS_TO_CART}
							trackEvent={trackEvent}
						>
							<Button
								variant="primary-cta"
								onClick={addToCart}
								containerProps={{ mb: 1 }}
								fillContainer={screenIsTiny}
								disabled={currentOrderItemCount === 0}
							>
								Add To Cart
							</Button>
						</EventTracker>
					</Box>
				</>
			)}
			<AddedToCartModalEcomm
				isOpen={showAddedToCartModal}
				partIds={partIds}
				onClose={() => setShowAddedToCartModal(false)}
			/>
		</PageContentWrapper>
	)
}

const QuickOrderPartRow: React.FC<QuickOrderPartRowProperties> = ({
	part,
	quantity,
	onQuantityChange,
}) => {
	const [partDetails] = useOnlinePartDetails(part.id)

	return (
		<div css={quickOrderListStyle}>
			<div css={partImageColumn}>
				<PartImage src={part.imageUrl} />
			</div>
			<div css={partDescriptionColumn}>
				<Link to={`/shop/parts/${part.id}`}>
					<div css={quickOrderPartTitleStyle}>{part.title}</div>
					<div css={partNumberColumn}>{part.onlinePartNumber}</div>
				</Link>
			</div>
			<div css={priceColumn}>
				{part.clearanceItemDiscount ?
					<>
						<PartClearancePriceEcomm
							price={part.clearanceItemDiscount}
							netPrice={part.netPrice}
							showClearanceLegend
						/>
					</>
				:	<QuickOrderPrice partId={part.id} price={part.netPrice} />}

				<Paragraph color="secondary" small ellipsisOverflow maxWidth={"140px"}>
					{UnitMapping(partDetails?.part?.unitOfMeasure ?? "")}
				</Paragraph>
			</div>
			<div css={quantityColumn}>
				<QuantityControl
					value={quantity}
					onChange={(newQuantity) => onQuantityChange(newQuantity, part)}
					onChooseZero={() => onQuantityChange(0, part)}
					labelPosition="left"
					useUpdateButton
					clearVariant="text"
				/>
			</div>
		</div>
	)
}

const quickOrderListStyle = (theme: Theme) => css`
	display: grid;
	grid-template-columns: 112px 1fr 10rem auto;
	align-items: center;
	grid-template-areas: "partImageColumn partDescriptionColumn priceColumn quantityColumn";
	border-bottom: 1px solid #eee;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	${theme.breakpoints.down("xs")} {
		justify-items: center;
		grid-template-columns: 1fr 1fr 1fr;
		grid-template-areas:
			"partImageColumn partImageColumn partImageColumn"
			"partDescriptionColumn partDescriptionColumn partDescriptionColumn"
			"priceColumn . quantityColumn";
		margin-bottom: 2rem;
	}
`
const partNumberColumn = (theme: Theme) => css`
	grid-area: partNumberColumn;
	color: #33373b;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	margin-top: 0.5rem;

	${theme.breakpoints.down("xs")} {
		margin: 0.5rem 0 0.875rem;
	}
`

const partImageColumn = (theme: Theme) => css`
	grid-area: partImageColumn;
	padding: 1rem;
	width: 100%;
	background: white;
	border-radius: 4px;
	border: 1px solid #e5e6e6;
	max-width: 88px;
	max-height: 88px;
	aspect-ratio: 1/1;

	img {
		object-fit: contain;
		max-width: 60px;
		max-height: 60px;
	}

	${theme.breakpoints.down("xs")} {
		max-width: 100%;
		max-height: 100%;
		padding: 46px;
		justify-items: center;
		align-content: center;
		margin-bottom: 26px;
		aspect-ratio: 1 / 1;

		img {
			max-width: 20rem;
			max-height: 100%;
			aspect-ratio: 1 / 1;
		}
	}
`
const partDescriptionColumn = (theme: Theme) => css`
	grid-area: partDescriptionColumn;
	width: 100%;
	padding-right: 24px;

	${theme.breakpoints.down("xs")} {
		padding-right: 0;
	}
`
const quantityColumn = (theme: Theme) => css`
	grid-area: quantityColumn;
	text-align: right;
	${theme.breakpoints.down("xs")} {
		text-align: left;
	}
`
const priceColumn = (theme: Theme) => css`
	grid-area: priceColumn;
	padding-left: 1rem;
	text-align: right;
	margin-right: 3rem;

	${theme.breakpoints.down("xs")} {
		padding-left: 0rem;
		margin-right: 0;
	}
`

const quickOrderParagraphStyle = css`
	color: #4b5563;
	font-size: 16px;
	font-style: normal;
	font-weight: 500;
`

const orderAddedCheckStyle = css`
	margin-right: 0.5rem;
`
const nestedButtonStyle = ({ palette }: Theme) => css`
	color: ${palette.primary.main};
	font-weight: bold;
	text-decoration: underline;
	background: none;
	border: 0;
	padding: 0;
	&:hover {
		color: ${palette.primary.light};
	}
`

const quickOrderTitleStyle = css`
	color: #111827;
	font-size: 28px;
	font-style: normal;
	font-weight: 700;
`

const quickOrderPartTitleStyle = css`
	color: #111827;
	font-size: 20px;
	font-style: normal;
	font-weight: 700;
	line-height: 28px;
	letter-spacing: -0.4px;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2; /* number of lines to show */
	line-clamp: 2;
	-webkit-box-orient: vertical;
	text-overflow: ellipsis;
`
